import { anonSupabase as supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { useEffect, useState } from 'react'

interface DemoSession {
  id: string
  target_role: string
  difficulty: string
  overall_score: number | null
  demo_persona: string | null
  completed_at: string | null
}

interface DemoReport {
  session_id: string
  feedback_json: {
    summary: string
    strengths: string[]
    improvements: string[]
    rewrite_tip: string
    example_stronger_result: string | null
  }
}

interface DemoScoreAgg {
  session_id: string
  situation: number
  task: number
  action: number
  result: number
}

const DIFFICULTY_LABELS: Record<string, string> = {
  entry: 'Entry',
  mid: 'Mid',
  senior: 'Senior',
}

const ROLE_CATEGORIES: Record<string, string[]> = {
  All: [],
  Engineering: ['Software Engineer', 'Data Engineer', 'DevOps Engineer', 'QA Engineer'],
  Business: ['Business Analyst', 'Operations Manager', 'Strategy Analyst', 'Management Consultant'],
  Product: ['Product Manager', 'Product Designer', 'UX Researcher'],
  Finance: ['Finance Analyst', 'Investment Banking Analyst', 'Accountant', 'Financial Analyst'],
  Marketing: ['Marketing Manager', 'Brand Manager', 'Growth Manager', 'Digital Marketer'],
}

function scoreColor(score: number): string {
  if (score >= 4.5) return 'text-green-400'
  if (score >= 3.5) return 'text-emerald-400'
  if (score >= 2.5) return 'text-amber-400'
  return 'text-red-400'
}

function scoreBg(score: number): string {
  if (score >= 4.5) return 'bg-green-400/10 border-green-400/30'
  if (score >= 3.5) return 'bg-emerald-400/10 border-emerald-400/30'
  if (score >= 2.5) return 'bg-amber-400/10 border-amber-400/30'
  return 'bg-red-400/10 border-red-400/30'
}

function ScoreBar({ label, score, weight }: { label: string; score: number; weight: string }) {
  const pct = Math.round(((score - 1) / 4) * 100)
  const isHigh = score >= 4
  const isLow = score < 2.5
  return (
    <div className="flex items-center gap-3">
      <div className="w-20 text-xs text-muted-foreground flex items-center justify-between">
        <span>{label}</span>
        <span className="text-[10px] opacity-50">{weight}</span>
      </div>
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full',
            isHigh ? 'bg-[#d4f53c]' : isLow ? 'bg-orange-400' : 'bg-slate-400'
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div
        className={cn(
          'text-xs font-mono w-8 text-right',
          isHigh ? 'text-[#d4f53c]' : isLow ? 'text-orange-400' : 'text-muted-foreground'
        )}
      >
        {score.toFixed(1)}
      </div>
    </div>
  )
}

export default function SampleReportsPage() {
  const [sessions, setSessions] = useState<DemoSession[]>([])
  const [reports, setReports] = useState<Record<string, DemoReport>>({})
  const [scores, setScores] = useState<Record<string, DemoScoreAgg>>({})
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('All')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      const { data: sessionRows } = await supabase
        .from('sessions')
        .select('id, target_role, difficulty, overall_score, demo_persona, completed_at')
        .eq('is_demo', true)
        .eq('status', 'completed')
        .order('overall_score', { ascending: false })

      if (!sessionRows || sessionRows.length === 0) {
        setLoading(false)
        return
      }

      setSessions(sessionRows as DemoSession[])

      const ids = sessionRows.map((s) => s.id)

      const [{ data: reportRows }, { data: scoreRows }] = await Promise.all([
        supabase
          .from('reports')
          .select('session_id, feedback_json')
          .in('session_id', ids)
          .eq('is_demo', true),
        supabase
          .from('scores')
          .select('session_id, situation_score, task_score, action_score, result_score')
          .in('session_id', ids),
      ])

      const reportMap: Record<string, DemoReport> = {}
      for (const r of reportRows ?? []) {
        reportMap[r.session_id] = r as DemoReport
      }

      const scoreMap: Record<string, DemoScoreAgg> = {}
      for (const sid of ids) {
        const sessionScores = (scoreRows ?? []).filter((s) => s.session_id === sid)
        if (sessionScores.length === 0) continue
        const avg = (key: keyof (typeof sessionScores)[0]) =>
          sessionScores.reduce((sum, s) => sum + (s[key] as number), 0) / sessionScores.length
        scoreMap[sid] = {
          session_id: sid,
          situation: Math.round(avg('situation_score') * 10) / 10,
          task: Math.round(avg('task_score') * 10) / 10,
          action: Math.round(avg('action_score') * 10) / 10,
          result: Math.round(avg('result_score') * 10) / 10,
        }
      }

      setReports(reportMap)
      setScores(scoreMap)
      setLoading(false)
    }

    load()
  }, [])

  const filteredSessions = sessions.filter((s) => {
    if (activeCategory === 'All') return true
    const roles = ROLE_CATEGORIES[activeCategory] ?? []
    return roles.some((r) => s.target_role?.toLowerCase().includes(r.toLowerCase()))
  })

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <a
            href="/"
            className="text-sm text-muted-foreground hover:text-foreground mb-6 inline-block"
          >
            ← Back to RoleRound
          </a>
          <h1 className="text-3xl font-bold mb-2">Sample Interview Reports</h1>
          <p className="text-muted-foreground max-w-xl">
            Real interview sessions run through RoleRound — scored and analyzed exactly as yours
            will be. See what the feedback looks like before you practice.
          </p>
          <a
            href="/login"
            className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium px-5 py-2.5 rounded-lg hover:opacity-90 transition-opacity text-sm"
          >
            Practice for free →
          </a>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {Object.keys(ROLE_CATEGORIES).map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat)}
              className={cn(
                'px-4 py-1.5 rounded-full text-sm font-medium border transition-colors',
                activeCategory === cat
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/40'
              )}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(['a', 'b', 'c', 'd', 'e', 'f'] as const).map((k) => (
              <div
                key={k}
                className="h-40 rounded-xl border border-border bg-muted/30 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && filteredSessions.length === 0 && (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg mb-2">Sample reports coming soon.</p>
            <p className="text-muted-foreground text-sm">
              Practice free now — you'll get the same detailed feedback on your own sessions.
            </p>
            <a
              href="/login"
              className="mt-4 inline-block text-primary underline underline-offset-2 text-sm"
            >
              Start practicing →
            </a>
          </div>
        )}

        {/* Gallery */}
        {!loading && filteredSessions.length > 0 && (
          <div className="space-y-4">
            {filteredSessions.map((session) => {
              const report = reports[session.id]
              const score = scores[session.id]
              const isExpanded = expandedId === session.id
              const overallScore = session.overall_score ?? 0

              return (
                <div
                  key={session.id}
                  className={cn(
                    'border rounded-xl overflow-hidden transition-all',
                    scoreBg(overallScore)
                  )}
                >
                  {/* Card header */}
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : session.id)}
                    className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div
                        className={cn(
                          'text-2xl font-bold font-mono tabular-nums',
                          scoreColor(overallScore)
                        )}
                      >
                        {overallScore.toFixed(1)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{session.target_role}</div>
                        <div className="text-xs text-muted-foreground">
                          {DIFFICULTY_LABELS[session.difficulty] ?? session.difficulty} level
                          {session.completed_at
                            ? ` · ${new Date(session.completed_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
                            : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-muted-foreground hidden sm:block">
                        {isExpanded ? 'Hide report' : 'View report'}
                      </span>
                      <span className="text-muted-foreground">{isExpanded ? '↑' : '↓'}</span>
                    </div>
                  </button>

                  {/* Expanded report */}
                  {isExpanded && report && (
                    <div className="px-6 pb-6 border-t border-border/50 space-y-6 pt-5">
                      {/* STAR scores */}
                      {score && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3">
                            STAR Breakdown
                          </div>
                          <div className="space-y-2">
                            <ScoreBar label="Situation" score={score.situation} weight="20%" />
                            <ScoreBar label="Task" score={score.task} weight="15%" />
                            <ScoreBar label="Action" score={score.action} weight="40%" />
                            <ScoreBar label="Result" score={score.result} weight="25%" />
                          </div>
                        </div>
                      )}

                      {/* Summary */}
                      {report.feedback_json?.summary && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">
                            Summary
                          </div>
                          <p className="text-sm text-foreground">{report.feedback_json.summary}</p>
                        </div>
                      )}

                      {/* Strengths */}
                      {report.feedback_json?.strengths?.length > 0 && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">
                            Strengths
                          </div>
                          <ul className="space-y-1">
                            {report.feedback_json.strengths.map((s) => (
                              <li
                                key={s.slice(0, 40)}
                                className="text-sm text-foreground flex gap-2"
                              >
                                <span className="text-[#d4f53c] shrink-0">✓</span>
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Improvements */}
                      {report.feedback_json?.improvements?.length > 0 && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">
                            Improvements
                          </div>
                          <ul className="space-y-1">
                            {report.feedback_json.improvements.map((s) => (
                              <li
                                key={s.slice(0, 40)}
                                className="text-sm text-foreground flex gap-2"
                              >
                                <span className="text-orange-400 shrink-0">↑</span>
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Rewrite tip */}
                      {report.feedback_json?.rewrite_tip && (
                        <div className="rounded-lg bg-muted/50 p-4 border border-border/50">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-1">
                            Coaching tip
                          </div>
                          <p className="text-sm text-foreground">
                            {report.feedback_json.rewrite_tip}
                          </p>
                        </div>
                      )}

                      <div className="pt-2 border-t border-border/50">
                        <a
                          href="/login"
                          className="text-sm text-primary underline underline-offset-2 hover:opacity-80"
                        >
                          Get feedback like this on your own answers →
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Expanded but no report yet */}
                  {isExpanded && !report && (
                    <div className="px-6 pb-6 pt-4 border-t border-border/50 text-sm text-muted-foreground">
                      Report not yet available for this session.
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer CTA */}
      {!loading && filteredSessions.length > 0 && (
        <div className="border-t mt-16">
          <div className="max-w-5xl mx-auto px-6 py-12 text-center">
            <h2 className="text-2xl font-bold mb-2">Ready to see your own report?</h2>
            <p className="text-muted-foreground mb-6">
              3 free sessions every 30 days. No credit card. Feedback in minutes.
            </p>
            <a
              href="/login"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
            >
              Start practicing free →
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
